import {
    GDFCcfpGeometryType,
    GDFCustomDrawShape,
    GDFFcaShape,
    GDFFrame,
    GDFFrontType,
    GDFHotPollyShape,
    GDFHotPollyTypes,
    GDFPalletCode,
    GDFProductType,
    GDFRecord,
    GDFRecordType,
    GDFTapsType,
    products,
    SelectedWeatherItem,
    WeatherLayerPlugin,
    WeatherStoreProductFrameInfo,
    WeatherStoreProductInfo
} from '@fe/weather';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { Window } from '@progress/kendo-react-dialogs';
import React from 'react';
import { EventBus } from '../../app/contexts/event-bus';
import useWeatherService from '../hooks/use-weather-service';
import { ChevronDownOutline, ChevronRightOutline } from '../../icons/icons';

const GdfViewer = () => {
    const [visible, setVisible] = React.useState(false);
    const weatherService = useWeatherService();
    const [selectedItem, setSelectedItem] = React.useState<SelectedWeatherItem | null>(null);
    EventBus.on(
        'debug-gdf',
        React.useCallback((e) => {
            setVisible(true);
            setSelectedItem(e.item);
        }, [])
    );

    if (!visible || !selectedItem) {
        return <></>;
    }

    return (
        <>
            <Window onClose={() => setSelectedItem(null)} title="GDF debugger" initialHeight={800} initialWidth={600}>
                <GDFItem item={selectedItem.weatherStore!} service={weatherService} />
            </Window>
        </>
    );
};

export const GDFItem = (props: { item: WeatherStoreProductInfo; service: WeatherLayerPlugin }) => {
    const { item } = props;
    const [expand, setExpand] = React.useState(true);
    const [expandFrames, setExpandFrames] = React.useState(true);

    return (
        <div key={item.id} className="w-100 py-1 d-flex gap-2 flex-column border-top">
            <div className="d-flex gap-1 align-items-center">
                <Button
                    svgIcon={expand ? ChevronDownOutline : ChevronRightOutline}
                    className="button-small"
                    fillMode={'flat'}
                    onClick={() => setExpand(!expand)}></Button>
                <div>ID: 0x{item.id.toString(16)}</div>
                <strong>{products[item.id]?.name ?? 'Name not found ⚠'}</strong>
                <div className="flex-grow-1"></div>

                {item.stats && (
                    <>
                        <Chip>
                            Comp/Decom: {formatKb(item.stats.compressedSize)}/{formatKb(item.stats.decompressedSize)}
                        </Chip>
                    </>
                )}
            </div>

            {expand && (
                <div className="ps-4">
                    <div className="d-flex gap-2">
                        <Button
                            svgIcon={expandFrames ? ChevronDownOutline : ChevronRightOutline}
                            className="button-small"
                            fillMode={'flat'}
                            onClick={() => setExpandFrames(!expandFrames)}
                        />
                        <div>Frames: {item.frames.length}</div>
                    </div>
                    {expandFrames && (
                        <div>
                            {item.frames.map((x, i) => (
                                <div key={i}>
                                    <GDFFrameView frame={x} id={item.id} service={props.service} idx={i} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function GDFFrameView(props: { id: number; frame: WeatherStoreProductFrameInfo; service: WeatherLayerPlugin; idx: number }) {
    const [expanded, setExpanded] = React.useState(false);
    const [expandedFrame, setExpandedFrame] = React.useState<GDFFrame | null>(null);

    const toggleFrame = async () => {
        if (expanded) {
            setExpanded(false);
        } else {
            const frame = await props.service.getFrame(props.id, props.idx);
            if (frame) {
                setExpandedFrame(frame);
                setExpanded(true);
            }
        }
    };

    React.useEffect(() => {
        if (props.idx === 0 && !expanded) {
            toggleFrame();
        }
    }, []);

    return (
        <div>
            <div className="ps-5 pe-2 d-flex gap-1 align-items-center">
                <Button
                    svgIcon={expanded ? ChevronDownOutline : ChevronRightOutline}
                    className="button-small"
                    fillMode={'flat'}
                    onClick={toggleFrame}
                />
                <div>
                    <span>
                        {GDFProductType[props.frame.header.productType]?.toUpperCase() ?? `UNKNOWN (${props.frame.header.productType})`}
                    </span>
                    <span>, Valid from: {props.frame.times.validFrom ?? '??'}</span>
                    <span>, Valid to: {props.frame.times.validTo ?? '??'}</span>
                    <span>, Created at: {props.frame.times.createdAt ?? '??'}</span>
                    <span>
                        , Palette: <strong>{GDFPalletCode[props.frame.header.productPallet].toUpperCase()}</strong> (
                        {props.frame.header.productPallet ?? '??'}/0x{props.frame.header.productPallet.toString(16)})
                    </span>
                    <span>, Record count: {props.frame.recordCount}</span>
                    {/* (count: {x.records.length}, max: {x.header.maxPoints}) */}
                    {expanded && <span>, Items: {expandedFrame!.records.length}</span>}
                </div>
            </div>
            {expanded && (
                <div className="ps-5">
                    <div className="ps-5">
                        {expandedFrame!.records.map((x, i) => (
                            <div key={i}>
                                <GDFRecordComponent record={x} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function GDFRecordComponent(props: { record: GDFRecord }) {
    const { record } = props;
    if (record.type === GDFRecordType.hotpolyhi) {
        return (
            <div>
                <span>
                    {GDFRecordType[record.type]} ({record.type})
                </span>
                {!record.reduced && <span>: Shape: {GDFHotPollyShape[record.shape]}</span>}
                <span>
                    , PolyType: {GDFHotPollyTypes[record.polyType]} ({record.polyType})
                </span>
                <span>, Points: {record.points.length}</span>
                {!record.reduced && <span>, Id: {record.id}</span>}
                {!record.reduced && <span>, Sev: {record.severity}</span>}

                {/* <span>, Start: {record.startTime?.toISOString()}</span>
                <span>, End: {record.endTime?.toISOString()}</span> */}
            </div>
        );
    }

    if (record.type === GDFRecordType.datetime2) {
        return (
            <div>
                <span>
                    {GDFRecordType[record.type]} ({record.type})
                </span>
                <span>Valid from {record.validFrom}</span>
                <span>, Valid to {record.validTo}</span>
                <span>, Created at {record.createdAt}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.datetime) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span> {record.time}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.navinfo2) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})
                <span>
                    {' '}
                    Width: {record.width}, Height: {record.height}
                </span>
                <span>
                    , Bounds:
                    {'['}
                    {record.lonLeft.toFixed(5)}, {record.latBottom.toFixed(5)}, {record.lonRight.toFixed(5)}, {record.latTop.toFixed(5)}
                    {']'}
                </span>
            </div>
        );
    }

    if (record.type === GDFRecordType.rle1) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})
                <span>
                    , Line: {record.line}, Bytes: {record.data.byteLength}
                </span>
            </div>
        );
    }
    if (record.type === GDFRecordType.navinfo) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})
                <span>
                    , Lines: {record.lines}, Elements: {record.elements}, Center {'['}
                    {record.centerLon.toFixed(5)},{record.centerLat.toFixed(5)}
                    {']'}, Deg/line: {record.degreesPerLine}, Deg/element: {record.degreesPerElement}
                </span>
            </div>
        );
    }

    if (record.type === GDFRecordType.legend) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span>, Text: "{record.text}"</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.title) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span>, Text: "{record.text}"</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.winds) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Dir: {record.direction}</span>
                <span>, Temp: {record.temperature}</span>
                <span>, Alt: {record.altitude}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.windssm) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Dir: {record.direction}</span>
                <span>, Temp: {record.temperature}</span>
                <span>, Alt: {record.altitude}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.metar) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>Id: {record.id}</span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Decoded: {record.isDecoded.toString()}</span>
                <span>, SubType: {record.subType}</span>
                <span>, WindSpeed: {record.windSpeed}</span>
                <span>, Altimeter: {record.altimeter}</span>
                <span>, Ceiling: {record.ceiling}</span>
                <span>, Temp: {record.temperature}</span>
                {/* <span>
                    , Text: '{record.text}'
                </span> */}
            </div>
        );
    }

    if (record.type === GDFRecordType.taf) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>Id: {record.id}</span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Cat: {record.flightCategory.toString()}</span>
                <span>, Text: {record.text}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.ccfp) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Points: [{record.points.length}]</span>
                <span>, Type: {GDFCcfpGeometryType[record.geometryType]}</span>
                <span>, Cov: {record.coverage}</span>
                <span>, prob: {record.prob}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.delay) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Airport: {record.airportId}</span>
                <span>, Elements: {record.elements.length}</span>
            </div>
        );
    }
    if (record.type === GDFRecordType.diversion) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Airport: {record.airportId}</span>
                <span>
                    , Elements: {record.elements.length}, To: {record.toCount} From:{record.fromCount}
                </span>
                <ul>
                    {record.elements.map((x, i) => (
                        <li key={i}>
                            {x.acid} {x.type == 'T' ? 'to' : 'from'} {x.airportToFrom}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    if (record.type === GDFRecordType.rvr) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Airport: {record.airportId}</span>
                <span>, Elements: {record.elements.length}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.hotpoly) {
        if (!record.reduced) {
            return (
                <div>
                    <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                    <span>
                        {' '}
                        Type:{' '}
                        <strong>
                            {GDFHotPollyTypes[record.polyType]} ({record.polyType})
                        </strong>
                    </span>
                    <span>
                        , Shape:{' '}
                        <strong>
                            {GDFHotPollyShape[record.shape]} ({record.shape})
                        </strong>
                    </span>
                    <span>
                        , Points: ({record.points.length}) [{record.points.map((x) => `[${x[0]}, ${x[1]}]`).join(', ')}]
                    </span>
                    <span>, Label: {record.label}</span>
                    <span>, Id: {record.id}</span>
                    {/* <span>, Detail: {record.detail}</span> */}
                </div>
            );
        }
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, reduced</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.label) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Text: {record.text}</span>
                <span>, VA: {record.verticalAlign}</span>
                <span>, HA: {record.horizontalAlign}</span>
                <span>
                    , Offset: [{record.lonOffset}, {record.latOffset}]
                </span>
                <span>, Type: {record.coordType}</span>
            </div>
        );
    }
    if (record.type === GDFRecordType.pirep2) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point from: [{record.pointFrom[0]}, {record.pointFrom[1]}]
                </span>
                <span>
                    , Point to: [{record.pointTo[0]}, {record.pointTo[1]}]
                </span>
                <span>, Sky: {record.skyCover ?? 'null'}</span>
                <span>, Vis: {record.visibility ?? 'null'}</span>
                <span>, Temp: {record.temp ?? 'null'}</span>
                <span>, Cloud top: {record.cloudTop ?? 'null'}</span>
                <span>, Cloud base: {record.cloudBase ?? 'null'}</span>
                <span>, Turb sev: {record.turbulenceSeverity ?? 'null'}</span>
                <span>, Icing sev: {record.icingSeverity ?? 'null'}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.pirep) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point from: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Alt: {record.altitude ?? 'null'}</span>
                <span>, Icing: {record.icingIntensity ?? 'null'}</span>
                <span>, Turb: {record.turbIntensity ?? 'null'}</span>
                <span>, Time: {record.time ?? 'null'}</span>
                <span>, Wind: {record.wind ?? 'null'}</span>
                <span>, Wind dir: {record.windDir ?? 'null'}</span>
                <span>, Temp: {record.temp ?? 'null'}</span>
                <span>, Text: {record.text || 'null'}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.fca) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point from: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Shape: {GDFFcaShape[record.shape]}</span>
                <span>, Points: {record.points.length}</span>
                <span>, Name: '{record.name || 'null'}'</span>
                <span>, Label: '{record.label || 'null'}'</span>
                <span>, Detail: '{record.detail || 'null'}'</span>
                <span>, Ceiling: {record.ceiling ?? 'null'}</span>
                <span>, Floor: {record.floor ?? 'null'}</span>
                <span>, AvgDelay: {record.avgDelay ?? 'null'}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.radsum) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point from: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Intensity: '{record.intensity ?? 'null'}'</span>
                <span>, Tops: '{record.tops ?? 'null'}'</span>
                <span>, Dir: '{record.dir ?? 'null'}'</span>
                <span>, Zoom: {record.zoom ?? 'null'}</span>
                <span>, Speed: {record.speed ?? 'null'}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.custom) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point: [{record.point[0]}, {record.point[1]}] ({record.points.length})
                    <span>, Shape: {GDFCustomDrawShape[record.shape]}</span>
                </span>
            </div>
        );
    }

    if (record.type === GDFRecordType.iataturb) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point from: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Altitude: {record.altitude ?? 'null'}</span>
                <span>, Wind speed: {record.windSpeed ?? 'null'}</span>
                <span>, Temp: {record.temp ?? 'null'}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmovolcano) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Point from: [{record.point[0]}, {record.point[1]}]
                </span>
                <span>, Name: {record.name || 'null'}</span>
                <span>, Id: {record.id || 'null'}</span>
                <span>, Summit: {record.summit || 'null'}</span>
                <span>, Adv: {record.advisory || 'null'}</span>
                <span>, Details: {record.details || 'null'}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.poly) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Points: {record.points}</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmohzdvol) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Point: [{record.point}]</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmostorm) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Point: [{record.point}]</span>
                <span>, Name: [{record.name}]</span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmotrop) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Point: [{record.point}]</span>
                <span>, Type: [{record.subType}]</span>
                <span>, Height: [{record.height}]</span>
            </div>
        );
    }
    if (record.type === GDFRecordType.wmocat) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Sev: [{record.severity}]</span>
                <span>, Base: [{record.base}]</span>
                <span>, Top: [{record.top}]</span>
                <span>
                    , Points: <code>[{record.points.map((x) => '[' + x + ']')}]</code>
                </span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmocloud) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Points: <code>[{record.points.map((x) => '[' + x + ']')}]</code>, Label: {record.label}
                </span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmojetstream) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Points: <code>[{record.points.map((x) => x.point).map((x) => '[' + x + ']')}]</code>
                </span>
            </div>
        );
    }
    if (record.type === GDFRecordType.front) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Points: <code>[{record.points.map((x) => '[' + x + ']').join(',')}]</code>, Type: {GDFFrontType[record.frontType]}(
                    {record.frontType})
                </span>
            </div>
        );
    }
    if (record.type === GDFRecordType.lightning) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Count: {record.count}</span>
            </div>
        );
    }
    if (record.type === GDFRecordType.taps) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>
                    , Type: <strong>{GDFTapsType[record.turbType]}</strong>
                </span>
                <span>, Point: [{record.point.toString()}]</span>
                <span>, Time: {record.reportTime}</span>
                <span>, Alt: {record.altitude}</span>
                <span>
                    , Speed: {record.windSpeed} kt at {record.windDir}deg
                </span>
            </div>
        );
    }

    if (record.type === GDFRecordType.wmovolcano2) {
        return (
            <div>
                <span>{GDFRecordType[record.type]}</span>({record.type})<span></span>
                <span>, Point: [{record.point.toString()}]</span>
                <span>, Name: {record.name}</span>
                <span>, Properties: {record.properties}</span>
                <span>, AdvNum: {record.advisory}</span>
                <span>, Report time: {record.reportTime}</span>
                <span>, Next report: {record.nextReportTime}</span>
                <span>, concentrations: {record.clouds.map(x => x.concentration).join(', ')}</span>
            </div>
        );
    }
    return (
        <div>
            {GDFRecordType[record.type] ?? '???'} ({record.type}) - Not parsed ⚠
        </div>
    );
}

function formatKb(input: number): string {
    if (input < 1000) {
        return input + 'B';
    }
    if (input < 1000000) {
        return Math.round(input / 1000).toLocaleString('en-US') + 'KB';
    }
    return (input / 1_000_000).toPrecision(3) + 'MB';
}

export default GdfViewer;